import React, { useState, useCallback, useMemo, memo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  IconButton,
  Chip,
  Tooltip,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { DragIndicator } from "@mui/icons-material";
import CustomTimeInputField from "./CustomTimeInputFields";
import CalendarManagementService from "../services/calendar_management.service";
import arrowlineleft from "../assets/icons/ArrowLineLeft-s.svg";
import ArrowLineRight from "../assets/icons/ArrowLineRight-s.svg";

// Define status mappings for the grid
const STATUS_MAPPING = {
  B: "Bank Holiday",
  S: "Sick Leave",
  H: "Holiday",
  O: "Day Off",
};

// Create a theme for the buttons
const theme = createTheme({
  palette: {
    dark: {
      main: "#333333",
      contrastText: "#ffffff",
    },
  },
});

// Create event handler factories outside the component
const createHandleDragStart =
  (setPointerLeft, setPointerTop, setIsDragging, setDraggedValue) =>
  ($event, dragData) => {
    // Always update pointer position for dragging
    setPointerLeft($event.pageX);
    setPointerTop($event.pageY);

    // Update the dragged value regardless of whether we're already dragging
    // This ensures we always have the latest data
    setIsDragging(true);

    // Handle both old format and new format from VirtualizedTable
    if (dragData.columnId) {
      // New format from VirtualizedTable
      const { dateIndex, columnId, therapist } = dragData;

      // Don't allow dragging coverage cells or date cells
      if (columnId === "date" || columnId.endsWith("_coverage")) {
        setIsDragging(false);
        return;
      }

      const [zoneIndex, therapistIndex] = columnId.split("_");
      const dragValue = therapist?.value || "N/A";

      setDraggedValue({
        dateIndex,
        zoneIndex,
        therapistIndex,
        columnId,
        therapist,
        value: dragValue,
      });
    } else {
      // Old format from original table
      const { dateIndex, zoneIndex, therapistIndex, therapist } = dragData;
      const dragValue = therapist?.value || "N/A";

      setDraggedValue({
        dateIndex,
        zoneIndex,
        therapistIndex,
        columnId: `${zoneIndex}_${therapistIndex}`, // Create columnId for consistency
        therapist,
        value: dragValue,
      });
    }

    // Add global pointer move and pointer up handlers
    const handlePointerMove = (e) => {
      setPointerLeft(e.pageX);
      setPointerTop(e.pageY);
    };

    const handlePointerUp = () => {
      // Don't immediately clear dragging state to allow the drop handler to use it
      setTimeout(() => {
        setIsDragging(false);
        setDraggedValue(null);
      }, 50);

      // Remove event listeners when drag ends
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };

    // Remove any existing listeners before adding new ones
    document.removeEventListener("pointermove", handlePointerMove);
    document.removeEventListener("pointerup", handlePointerUp);

    // Add the new listeners
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
  };

// Create cell mouse enter handler factory
const createHandleCellMouseEnter =
  (isDragging, draggedValue, rows, setRows) =>
  (dateIndex, zoneIndex, therapistIndex, therapist) => {
    if (
      isDragging &&
      draggedValue &&
      therapistIndex !== "coverage" &&
      (draggedValue?.dateIndex !== dateIndex ||
        draggedValue?.zoneIndex !== zoneIndex ||
        draggedValue?.therapistIndex !== therapistIndex)
    ) {
      console.log({ rows });

      const rowData = rows.get(dateIndex);
      if (rowData) {
        for (let [, value] of rowData) {
          if (value && value.has(therapistIndex)) {
            // Handle status values or time ranges
            console.log({ value, therapist, therapistIndex });
            if (value[therapistIndex]?.value !== draggedValue.value) {
              if (draggedValue.therapist) {
                // set copy of value instead
                value.set(therapistIndex, {
                  ...draggedValue.therapist,
                  changed: true,
                  id: therapistIndex,
                });
              }
            }
          }
          // if (draggedValue.value) {
          //   // Handle direct value (from dragging a chip)
          //   const isStatus = Object.values(STATUS_MAPPING).includes(draggedValue.value);

          //   // Create therapist data structure if needed
          //   // if (!therapist) {
          //   //   continue; // Skip if no therapist data
          //   // }
          //   console.log({ therapist })
          //   // Create a copy of the original data
          //   const updatedTherapist = { ...therapist };
          //   if (updatedTherapist.value === draggedValue.value) {
          //     if (isStatus) {
          //       // Handle status values (Day Off, Holiday, etc.)
          //       if (!updatedTherapist.changed) {
          //         updatedTherapist.old = therapist;
          //       }
          //       updatedTherapist.value = draggedValue.value + '';
          //       updatedTherapist.availability = {
          //         start: draggedValue.therapist?.availability?.start,
          //         end: draggedValue.therapist?.availability?.end,
          //       };
          //       updatedTherapist.changed = true;
          //       updatedTherapist.id = therapistIndex;
          //     } else {
          //       // Handle time ranges
          //       if (!updatedTherapist.changed) {
          //         updatedTherapist.old = therapist;
          //       }
          //       updatedTherapist.value = draggedValue?.value + '';
          //       updatedTherapist.availability = {
          //         start: draggedValue.therapist?.availability?.start,
          //         end: draggedValue.therapist?.availability?.end,
          //       };
          //       updatedTherapist.changed = true;
          //       updatedTherapist.id = therapistIndex;
          //     }

          //     value.set(therapistIndex, updatedTherapist);
          //   }

          // }
        }
        console.log({ rows });
        // Create a new reference to trigger re-render
        setRows(new Map(rows));
      }
    }
  };

// Create time input change handler factory
const createHandleTimeInputChange =
  (gridTherapists) => (returnedTherapist, returnedIndex) => {
    console.log({ gridTherapists, returnedTherapist, returnedIndex });
    const therapists = [...gridTherapists];
    const current = therapists.find((item) => item.id === returnedTherapist.id);
    console.log({ current });
    if (
      current &&
      current.calendar[returnedIndex]?.value &&
      current.calendar[returnedIndex]?.value !== returnedTherapist.value
    ) {
    }
  };

// Create button click handlers
const createHandleAddMonth =
  (currentDateForGrid, setCurrentDateForGrid) => () => {
    const date = new Date(currentDateForGrid);
    date.setMonth(currentDateForGrid.getMonth() + 1);
    setCurrentDateForGrid(date);
  };
const createHandleSubstractMonth =
  (currentDateForGrid, setCurrentDateForGrid) => () => {
    const date = new Date(currentDateForGrid);
    date.setMonth(currentDateForGrid.getMonth() - 1);
    setCurrentDateForGrid(date);
  };
const createHandleResetMonth =
  (currentDateForGrid, setCurrentDateForGrid) => () => {
    const date = new Date();
    date.setDate(1);
    if (
      currentDateForGrid.getMonth() !== date.getMonth() ||
      currentDateForGrid.getYear() !== date.getYear()
    ) {
      setCurrentDateForGrid(date);
    }
  };
const createHandleEditClick = (setGridEdit) => () => setGridEdit(true);
const createHandleSaveClick = (setGridEdit, data, api) => async () => {
  console.log({ data });
  let gridData = {};
  data.forEach((zone, date) => {
    const processed = [];
    zone.forEach((therapist, zoneId) => {
      therapist.forEach((data, therapistId) => {
        if (therapistId !== "coverage" && !processed.includes(therapistId)) {
          processed.push(therapistId);
          if (
            data.changed &&
            data.availability &&
            data.availability.start &&
            data.availability.end &&
            data.value
          ) {
            if (!Object.keys(gridData).includes(date)) {
              gridData[date] = {};
            }
            let value = data.value;
            if (Object.values(STATUS_MAPPING).includes(data.value)) {
              switch (data.value) {
                case "Day Off":
                  value = "OFF";
                  break;
                default:
                  value = data.value.replace(" ", "_").toUpperCase();
                  break;
              }
            }
            gridData[date][therapistId] = {
              availability: data.availability,
              value: value ?? data.value,
            };
            // if(!gridData.has(date)) {
            //   gridData.set(date, new Map());
            // }
            // gridData.get(date).set(therapistId, {availability: data.availability, value: data.value });
          }
        }
      });
    });
  });
  console.log({ gridData });
  await CalendarManagementService.createGridSlots({ gridData }, api);
  setGridEdit(false);
};
const createHandleCancelClick = (setGridEdit) => () => setGridEdit(false);

// Create pointer event handlers for the Paper component
const createHandlePointerUpCapture = (setIsDragging) => () =>
  setIsDragging(false);
const createHandlePointerMoveCapture =
  (isDragging, setPointerLeft, setPointerTop) => ($event) => {
    if (isDragging) {
      setPointerLeft($event.pageX);
      setPointerTop($event.pageY);
    }
  };

// Create drag ghost element factory
const createDragGhost = (isDragging, draggedValue, pointerLeft, pointerTop) => {
  if (!isDragging || !draggedValue) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: pointerLeft,
        top: pointerTop,
        transform: "translateX(-50%) translateY(-50%)",
        padding: "8px 12px",
        border: "2px solid #1976d2",
        borderRadius: "4px",
        backgroundColor: "#fff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
        zIndex: 9999,
        pointerEvents: "none",
        minWidth: "100px",
        fontWeight: "bold",
        color: "#1976d2",
      }}
    >
      {draggedValue.value}
    </div>
  );
};

const calculateDuration = (start, end) => {
  const parseTime = (timeStr) => {
    let [hour, minute] = timeStr.split(":").map(Number);
    return hour * 60 + minute;
  };

  const startTotalMinutes = parseTime(start);
  const endTotalMinutes = parseTime(end);
  const durationMinutes = endTotalMinutes - startTotalMinutes;
  const totalMinutes =
    durationMinutes >= 0 ? durationMinutes : 24 * 60 + durationMinutes;

  const durationHours = Math.floor(totalMinutes / 60);
  const durationRemMinutes = totalMinutes % 60;

  return `${durationHours}:${String(durationRemMinutes).padStart(2, "0")}`;
};

const strToTime = (timeStr) => {
  const [hours, minutes] = timeStr.split(":");
  return new Date(0, 0, 0, hours, minutes); // Use Date with dummy date to compare time only
};

const processTimeSegment = (hoursArray, start, end, duration) => {
  if (hoursArray.length === 0) {
    // First entry
    hoursArray.push({ start, end, duration });
    return;
  }

  // Try to merge with existing segments
  let merged = false;
  for (let i = 0; i < hoursArray.length; i++) {
    const segment = hoursArray[i];

    // Case 1: Current segment ends exactly where new one starts
    if (segment.end === start) {
      segment.end = end;
      segment.duration = calculateDuration(segment.start, segment.end);
      merged = true;
      break;
    }

    // Case 2: New segment ends exactly where current one starts
    if (end === segment.start) {
      segment.start = start;
      segment.duration = calculateDuration(segment.start, segment.end);
      merged = true;
      break;
    }

    // Case 3: Segments overlap
    const segmentStartTime = strToTime(segment.start);
    const segmentEndTime = strToTime(segment.end);
    const newStartTime = strToTime(start);
    const newEndTime = strToTime(end);

    if (newStartTime <= segmentEndTime && newEndTime >= segmentStartTime) {
      // Take the earlier start time and later end time
      segment.start = newStartTime < segmentStartTime ? start : segment.start;
      segment.end = newEndTime > segmentEndTime ? end : segment.end;
      segment.duration = calculateDuration(segment.start, segment.end);
      merged = true;
      break;
    }
  }

  // If couldn't merge with any existing segment, add as new
  if (!merged) {
    hoursArray.push({ start, end, duration });
  }
};

const calculateCoverageForZone = (
  dateIndex,
  zoneId,
  therapistSchedules,
  rows
) => {
  const coverage = { normalHours: [], extraHours: [] };

  // Define normal hours (08:00-18:00) and extra hours (18:00-22:00) boundaries
  const normalHoursStart = "08:00";
  const normalHoursEnd = "18:00";
  const extraHoursEnd = "22:00";

  // Create Date objects for time comparisons
  const normalStartTime = strToTime(normalHoursStart);
  const normalEndTime = strToTime(normalHoursEnd);
  const extraEndTime = strToTime(extraHoursEnd);

  // Process each therapist's schedule
  for (const { therapistId, schedule } of therapistSchedules) {
    if (!schedule) continue;

    // Skip status values (Bank Holiday, Sick Leave, etc.) from coverage calculation
    if (Object.values(STATUS_MAPPING).includes(schedule.value)) {
      continue;
    }

    // Skip schedules without availability data
    if (
      !schedule.availability ||
      !schedule.availability.start ||
      !schedule.availability.end
    ) {
      continue;
    }

    const { start, end } = schedule.availability;
    const startTime = strToTime(start);
    const endTime = strToTime(end);

    // Skip invalid time formats
    if (isNaN(startTime) || isNaN(endTime)) {
      continue;
    }

    // Case 1: Schedule is entirely within normal hours (08:00-18:00)
    if (startTime >= normalStartTime && endTime <= normalEndTime) {
      const duration = calculateDuration(start, end);
      processTimeSegment(coverage.normalHours, start, end, duration);
      continue;
    }

    // Case 2: Schedule is entirely within extra hours (18:00-22:00)
    if (startTime >= normalEndTime && endTime <= extraEndTime) {
      const duration = calculateDuration(start, end);
      processTimeSegment(coverage.extraHours, start, end, duration);
      continue;
    }

    // Case 3: Schedule spans both normal and extra hours
    if (startTime < normalEndTime && endTime > normalEndTime) {
      // Calculate durations for normal and extra portions
      const normalDuration = calculateDuration(
        start < normalStartTime ? normalHoursStart : start,
        normalHoursEnd
      );
      const extraDuration = calculateDuration(
        normalHoursEnd,
        end > extraEndTime ? extraHoursEnd : end
      );

      // Process normal hours portion (if any)
      if (startTime < normalEndTime) {
        const normalStart =
          startTime < normalStartTime ? normalHoursStart : start;
        processTimeSegment(
          coverage.normalHours,
          normalStart,
          normalHoursEnd,
          normalDuration
        );
      }

      // Process extra hours portion (if any)
      if (endTime > normalEndTime) {
        const extraEnd = endTime > extraEndTime ? extraHoursEnd : end;
        processTimeSegment(
          coverage.extraHours,
          normalHoursEnd,
          extraEnd,
          extraDuration
        );
      }
    }

    // Case 4: Schedule starts before normal hours but ends within normal hours
    else if (
      startTime < normalStartTime &&
      endTime <= normalEndTime &&
      endTime > normalStartTime
    ) {
      const duration = calculateDuration(normalHoursStart, end);
      processTimeSegment(coverage.normalHours, normalHoursStart, end, duration);
    }

    // Case 5: Schedule starts within normal hours but extends beyond extra hours
    else if (
      startTime >= normalStartTime &&
      startTime < normalEndTime &&
      endTime > extraEndTime
    ) {
      // Normal hours portion
      const normalDuration = calculateDuration(start, normalHoursEnd);
      processTimeSegment(
        coverage.normalHours,
        start,
        normalHoursEnd,
        normalDuration
      );

      // Extra hours portion
      const extraDuration = calculateDuration(normalHoursEnd, extraHoursEnd);
      processTimeSegment(
        coverage.extraHours,
        normalHoursEnd,
        extraHoursEnd,
        extraDuration
      );
    }

    // Case 6: Schedule spans the entire day (before normal hours to after extra hours)
    else if (startTime < normalStartTime && endTime > extraEndTime) {
      // Normal hours portion
      const normalDuration = calculateDuration(
        normalHoursStart,
        normalHoursEnd
      );
      processTimeSegment(
        coverage.normalHours,
        normalHoursStart,
        normalHoursEnd,
        normalDuration
      );

      // Extra hours portion
      const extraDuration = calculateDuration(normalHoursEnd, extraHoursEnd);
      processTimeSegment(
        coverage.extraHours,
        normalHoursEnd,
        extraHoursEnd,
        extraDuration
      );
    }
  }

  // Sort the hours arrays by start time
  coverage.normalHours.sort((a, b) => strToTime(a.start) - strToTime(b.start));
  coverage.extraHours.sort((a, b) => strToTime(a.start) - strToTime(b.start));
  console.log({ coverage });

  // Update the coverage for this zone
  rows.get(dateIndex).get(zoneId).set("coverage", coverage);
};

// Helper function to convert time string to minutes since 8:00
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return (hours - 8) * 60 + minutes;
};

// Helper function to calculate width percentage based on duration
const calculateWidthPercentage = (start, end) => {
  const totalMinutes = 14 * 60; // 14 hours (8:00 - 22:00) in minutes
  const durationMinutes = timeToMinutes(end) - timeToMinutes(start);
  return (durationMinutes / totalMinutes) * 100;
};

// CoverageChips component to visualize coverage with colored chips
const CoverageChips = memo(({ coverage }) => {
  if (
    !coverage ||
    (!coverage.normalHours.length && !coverage.extraHours.length)
  ) {
    return <div>No coverage data</div>;
  }

  // Container style for the chips row
  const containerStyle = {
    display: "flex",
    width: "100%",
    height: "20px",
    position: "relative",
    borderRadius: "4px",
    overflow: "hidden",
  };

  return (
    <Tooltip
      title={
        <div>
          <div>
            Normal Hours:{" "}
            {coverage.normalHours.map((h) => `${h.start}-${h.end}`).join(", ")}
          </div>
          <div>
            Extra Hours:{" "}
            {coverage.extraHours.map((h) => `${h.start}-${h.end}`).join(", ")}
          </div>
        </div>
      }
    >
      <div style={containerStyle}>
        {/* Normal hours chips (green) */}
        {coverage.normalHours.map((hour, index) => {
          const startMinutes = timeToMinutes(hour.start);
          const width = calculateWidthPercentage(hour.start, hour.end);

          return (
            <div
              key={`normal-${index}`}
              style={{
                position: "absolute",
                left: `${(startMinutes / (14 * 60)) * 100}%`,
                width: `${width}%`,
                height: "100%",
                backgroundColor: "#4caf50", // Green for normal hours
                borderRadius: "2px",
              }}
            />
          );
        })}

        {/* Extra hours chips (yellow) */}
        {coverage.extraHours.map((hour, index) => {
          const startMinutes = timeToMinutes(hour.start);
          const width = calculateWidthPercentage(hour.start, hour.end);

          return (
            <div
              key={`extra-${index}`}
              style={{
                position: "absolute",
                left: `${(startMinutes / (14 * 60)) * 100}%`,
                width: `${width}%`,
                height: "100%",
                backgroundColor: "#ADFF2F", // Yellow for extra hours
                borderRadius: "2px",
              }}
            />
          );
        })}
      </div>
    </Tooltip>
  );
});

/**
 * OptimizedCalendarGrid component for displaying a grid view of the calendar
 *
 * @param {Object} props - Component props
 * @param {Map} props.rows - Map of rows data for the grid
 * @param {Array} props.filteredZones - Array of filtered zones
 * @param {Array} props.filteredTherapists - Array of filtered therapists
 * @param {boolean} props.gridEdit - Whether the grid is in edit mode
 * @param {string} props.currentDateForGrid - current grid date
 * @param {function} props.setRows - Function to update the rows data
 * @param {function} props.setGridEdit - Function to update the gridEdit state
 * @param {function} props.setCurrentDateForGrid - Function to update the current grid date state
 * @param {Array} props.gridTherapists - Array of therapists for the grid
 */
const OptimizedCalendarGrid = memo(
  ({
    rows,
    filteredZones,
    filteredTherapists,
    gridEdit,
    setRows,
    setGridEdit,
    gridTherapists = [],
    currentDateForGrid,
    setCurrentDateForGrid,
    api,
  }) => {
    // State for drag and drop functionality
    const [isDragging, setIsDragging] = useState(false);
    const [draggedValue, setDraggedValue] = useState(null);
    const [pointerLeft, setPointerLeft] = useState(0);
    const [pointerTop, setPointerTop] = useState(0);

    // Create memoized event handlers
    const handleDragStart = useCallback(
      createHandleDragStart(
        setPointerLeft,
        setPointerTop,
        setIsDragging,
        setDraggedValue
      ),
      [setPointerLeft, setPointerTop, setIsDragging, setDraggedValue]
    );

    const handleCellMouseEnter = useCallback(
      createHandleCellMouseEnter(isDragging, draggedValue, rows, setRows),
      [isDragging, draggedValue, rows, setRows]
    );

    const handleTimeInputChange = useCallback(
      createHandleTimeInputChange(gridTherapists),
      [gridTherapists]
    );

    const handleEditClick = useCallback(createHandleEditClick(setGridEdit), [
      setGridEdit,
    ]);

    const handleAddMonth = useCallback(
      createHandleAddMonth(currentDateForGrid, setCurrentDateForGrid),
      [currentDateForGrid, setCurrentDateForGrid]
    );
    const handleSubstractMonth = useCallback(
      createHandleSubstractMonth(currentDateForGrid, setCurrentDateForGrid),
      [currentDateForGrid, setCurrentDateForGrid]
    );
    const handleResetMonth = useCallback(
      createHandleResetMonth(currentDateForGrid, setCurrentDateForGrid),
      [currentDateForGrid, setCurrentDateForGrid]
    );

    const handleSaveClick = useCallback(
      createHandleSaveClick(setGridEdit, rows, api),
      [setGridEdit, rows, api]
    );

    const handleCancelClick = useCallback(
      createHandleCancelClick(setGridEdit),
      [setGridEdit]
    );

    const handlePointerUpCapture = useCallback(
      createHandlePointerUpCapture(setIsDragging),
      [setIsDragging]
    );

    const handlePointerMoveCapture = useCallback(
      createHandlePointerMoveCapture(isDragging, setPointerLeft, setPointerTop),
      [isDragging, setPointerLeft, setPointerTop]
    );

    // Create drag ghost element
    const dragGhost = useMemo(
      () => createDragGhost(isDragging, draggedValue, pointerLeft, pointerTop),
      [isDragging, draggedValue, pointerLeft, pointerTop]
    );

    // Create memoized long press handler for cells
    const createCellLongPressHandler = useCallback(
      (dateIndex, zoneIndex, therapistIndex, therapist) => {
        let longPressTimer = null;

        // Handler for pointer down - starts the timer
        const handlePointerDown = ($event) => {
          if (gridEdit) {
            // Clear any existing timer
            if (longPressTimer) {
              clearTimeout(longPressTimer);
            }

            // Set a new timer for long press (500ms)
            longPressTimer = setTimeout(() => {
              handleDragStart($event, {
                dateIndex,
                zoneIndex,
                therapistIndex,
                therapist,
              });
            }, 200);
          }
        };

        // Handler for pointer up/leave - cancels the timer
        const handlePointerUpOrLeave = () => {
          if (longPressTimer) {
            clearTimeout(longPressTimer);
            longPressTimer = null;
          }
        };

        return {
          handlePointerDown,
          handlePointerUpOrLeave,
        };
      },
      [gridEdit, handleDragStart]
    );

    return (
      <Paper
        sx={{ width: "100%" }}
        onPointerUpCapture={handlePointerUpCapture}
        onPointerMoveCapture={handlePointerMoveCapture}
      >
        {/* Render the drag ghost outside the table to prevent flickering */}
        {dragGhost}

        <TableContainer sx={{ maxHeight: "calc(100vh - 206px)" }}>
          <Table
            stickyHeader
            aria-label="sticky table"
            sx={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  align="left"
                  colSpan={1}
                  rowSpan={2}
                  sx={{
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                    border: "1px solid #e0e0e0",
                    padding: "12px",
                    position: "sticky",
                    left: 0,
                    zIndex: 4,
                  }}
                >
                  <div className="group-cal" style={{ width: "190px" }}>
                    <div className="button-cal">
                      <div className="iconset-cal">
                        <img
                          onClick={handleSubstractMonth}
                          className="arrowlineleft-s-icon-cal"
                          alt="Previous Day"
                          src={arrowlineleft}
                        />
                      </div>
                    </div>
                    <div className="text-cal">
                      <div className="text1-cal">
                        {currentDateForGrid.toLocaleDateString("en-US", {
                          month: "short",
                          year: "numeric",
                        })}
                      </div>
                    </div>
                    <div className="button-cal">
                      <div className="iconset-cal">
                        <img
                          onClick={handleAddMonth}
                          className="arrowlineleft-s-icon-cal"
                          alt="Next Day"
                          src={ArrowLineRight}
                        />
                      </div>
                    </div>
                    <div className="button2-cal">
                      <div className="text-cal">
                        <div className="text1-cal" onClick={handleResetMonth}>
                          Today
                        </div>
                      </div>
                    </div>
                  </div>
                </TableCell>
                {filteredZones.map((zone) => (
                  <TableCell
                    key={zone.id}
                    align="center"
                    sx={{
                      textAlign: "center !important",
                      backgroundColor: "#f5f5f5",
                      fontWeight: "bold",
                      border: "1px solid #e0e0e0",
                      padding: "12px",
                    }}
                    colSpan={
                      filteredTherapists.filter(
                        (therapist) =>
                          therapist.zones.findIndex((z) => z.id === zone.id) !==
                          -1
                      ).length + 1
                    }
                  >
                    {zone.name}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {filteredZones.map((zone) => {
                  return (
                    <React.Fragment key={zone.id}>
                      {filteredTherapists
                        .filter(
                          (therapist) =>
                            therapist.zones.findIndex(
                              (z) => z.id === zone.id
                            ) !== -1
                        )
                        .map((therapist) => {
                          return (
                            <TableCell
                              key={therapist.id}
                              align={"left"}
                              sx={{
                                top: 50,
                                minWidth: "40px",
                                backgroundColor: "#f5f5f5",
                                fontWeight: "bold",
                                border: "1px solid #e0e0e0",
                                padding: "8px",
                              }}
                            >
                              {therapist.extra["full_name"]}
                            </TableCell>
                          );
                        })}
                      <TableCell
                        align={"left"}
                        sx={{
                          top: 50,
                          minWidth: "40px",
                          backgroundColor: "#f5f5f5",
                          fontWeight: "bold",
                          border: "1px solid #e0e0e0",
                          padding: "8px",
                        }}
                      >
                        Coverage
                      </TableCell>
                    </React.Fragment>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...rows].map(([dateIndex, row]) => {
                return (
                  <TableRow hover tabIndex={-1} key={dateIndex}>
                    <TableCell
                      align="left"
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: "#f9f9f9",
                        fontWeight: "bold",
                        border: "1px solid #e0e0e0",
                        padding: "8px",
                      }}
                    >
                      {new Date(dateIndex).toDateString()}
                    </TableCell>
                    {[...row].map(([zoneIndex, therapists]) => {
                      return (
                        <React.Fragment key={`${dateIndex}-${zoneIndex}`}>
                          {[...therapists].map(
                            ([therapistIndex, therapist]) => {
                              // Create handlers for this specific cell
                              const cellMouseEnterHandler = () =>
                                handleCellMouseEnter(
                                  dateIndex,
                                  zoneIndex,
                                  therapistIndex,
                                  therapist
                                );
                              const {
                                handlePointerDown,
                                handlePointerUpOrLeave,
                              } = createCellLongPressHandler(
                                dateIndex,
                                zoneIndex,
                                therapistIndex,
                                therapist
                              );
                              return (
                                <TableCell
                                  key={`${dateIndex}-${zoneIndex}-${therapistIndex}`}
                                  id={`${dateIndex}-${zoneIndex}-${therapistIndex}`}
                                  align={"left"}
                                  sx={{
                                    top: 57,
                                    minWidth: "40px",
                                    border: "1px solid #e0e0e0",
                                    padding: "8px",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                                    },
                                    ...(isDragging && {
                                      cursor: "copy",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(25, 118, 210, 0.12)",
                                        outline:
                                          "2px dashed rgba(25, 118, 210, 0.7)",
                                        boxShadow:
                                          "inset 0 0 5px rgba(25, 118, 210, 0.3)",
                                      },
                                    }),
                                  }}
                                  onMouseEnter={cellMouseEnterHandler}
                                  onPointerDown={handlePointerDown}
                                  onPointerUp={handlePointerUpOrLeave}
                                  onPointerLeave={handlePointerUpOrLeave}
                                >
                                  {gridEdit === false ? (
                                    therapistIndex === "coverage" ? (
                                      <CoverageChips coverage={therapist} />
                                    ) : (
                                      <Chip label={therapist?.value ?? "N/A"} />
                                    )
                                  ) : therapistIndex === "coverage" ? (
                                    <CoverageChips coverage={therapist} />
                                  ) : (
                                    <Box
                                      sx={
                                        {
                                          // display: 'flex',
                                          // justifyContent: 'space-between',
                                          // ...(isDragging && draggedValue?.dateIndex === dateIndex && draggedValue?.zoneIndex === zoneIndex && draggedValue?.therapistIndex === therapistIndex ? { position: 'absolute', left: pointerLeft, top: pointerTop, transform: 'translateX(-200px) translateY(-20px)' } : {})
                                        }
                                      }
                                    >
                                      <CustomTimeInputField
                                        onChange={handleTimeInputChange}
                                        data={{ therapist, dateIndex }}
                                      />
                                    </Box>
                                  )}
                                </TableCell>
                              );
                            }
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row-reverse",
            alignItems: "center",
            gap: 1,
            padding: "16px",
          }}
        >
          <ThemeProvider theme={theme}>
            {gridEdit === false ? (
              <Button
                color="dark"
                variant="contained"
                onClick={handleEditClick}
                endIcon={<EditIcon sx={{ height: 15 }} />}
              >
                Edit
              </Button>
            ) : (
              <>
                <Button
                  color="dark"
                  variant="contained"
                  onClick={handleSaveClick}
                  endIcon={<EditIcon sx={{ height: 15 }} />}
                >
                  Save
                </Button>
                <Button
                  color="dark"
                  variant="outlined"
                  onClick={handleCancelClick}
                  endIcon={<EditIcon sx={{ height: 15 }} />}
                >
                  Cancel
                </Button>
              </>
            )}
          </ThemeProvider>
        </Box>
      </Paper>
    );
  }
);

export default OptimizedCalendarGrid;
